/** React wrapper around the pure draft store (`changeSet.ts`), page-level so the draft
 * survives page-tab and view switches (one draft, one ledger, one save).
 *
 * - **Baselines** come from the record's edit_context (`baselines`, field space); the
 *   provider adds an `action:<key>` path per queued action (`actionFields`). Whenever
 *   they change — first load, refetch, the re-seed after a save — the draft REBASEs onto
 *   them, so a stored value that moved under an entry turns it stale instead of being
 *   silently overwritten.
 * - **sessionStorage mirror** at `console:draft:<kind>:<id>`: the entries (each carrying
 *   its baseline `from`), when they were last edited, the baselines' `generated_ts` and a
 *   schema version. Every access is guarded (storage can be absent or throw); a draft over
 *   the size cap is not mirrored and its older copy is removed, so a reload never restores
 *   an outdated draft. History is not mirrored. On mount a stored draft is restored —
 *   `restored` drives the "Restored N unsaved changes from 10:41 · Discard" banner — and
 *   re-checked by the first rebase onto the fresh baselines.
 * - Keyboard shortcuts are a page concern (`useRecordShortcuts`); the unsaved-changes
 *   guard is `useDraftUnsavedGuard` (the router's single blocker belongs to the page).
 * - **The store** is a small external store around `draftReducer`: `useDraft()` reads the
 *   whole state through the context (every consumer re-renders on every change), while
 *   `useDraftRecord(rec)` subscribes to ONE record's entries through
 *   `useSyncExternalStore` — `withEntry` keeps untouched records' maps by reference, so
 *   editing record A never re-renders a subscriber of record B. `useDraftStable()` hands
 *   the identity-stable half (dispatch, the store, the latest baselines) to components
 *   that must not re-render with the draft.
 * - **Record ids may be negative** (`alias:-1`): rows the draft creates. They survive a
 *   restore like any other record, and no temporary id the session issued is issued again:
 *   the mirror carries the session's temporary-id floor (`temp_id_floor`, only once one was
 *   issued), and `restoredDraftState` takes the lower of it and the ids the entries hold — so
 *   an id freed before the reload (a new row removed or undone) stays spent.
 * - `freshFrom` lets a record kind say what a rebase compares its entries with — the
 *   tour's adds tombstones for vanished rows and merges id lists (`mergedTo`). Default:
 *   every baseline value as it stands (`freshFromBaselines`).
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type Dispatch,
  type ReactElement,
  type ReactNode,
} from 'react';

import { useUnsavedChangesPrompt } from '@/hooks/useUnsavedChangesPrompt';

import {
  compileChangeset,
  countByStage,
  draftCount,
  draftReducer,
  freshFromBaselines,
  freshFromDrift,
  groupByRecord,
  hasStale,
  initialDraftState,
  pathBaselines,
  recordKey,
  restoredDraftState,
  undoRedoInfo,
  type ChangesetExtras,
  type DraftAction,
  type DraftEntry,
  type DraftLabelFormatter,
  type DraftRecordGroup,
  type DraftState,
  type FreshValue,
  type RecordDriftedField,
  type RecordKey,
  type StaleInfo,
  type UndoRedoInfo,
} from './changeSet';

/** The record kinds with a draft — one per record page; the mirror's key and the restore
 *  check carry it, so a draft never restores into another kind's record of the same id.
 *  `operator_precheck`: a tour operator's pre-check-in settings (its Pre-check-in tab) —
 *  record `tour_operator:<id>`. */
export type DraftKind =
  | 'booking'
  | 'order'
  | 'user'
  | 'tour'
  | 'activity'
  | 'activity_category'
  | 'itinerary'
  | 'operator_precheck';

/** Bump when the stored shape changes; older copies are discarded on read. */
export const DRAFT_STORAGE_VERSION = 1;
/** Mirror cap in characters of JSON — far above any real draft (60 changes ≈ 10 KB). */
export const DRAFT_STORAGE_MAX_CHARS = 256 * 1024;

export function draftStorageKey(kind: DraftKind, id: number): string {
  return `console:draft:${kind}:${id}`;
}

interface StoredDraft {
  v: number;
  kind: DraftKind;
  id: number;
  /** When the draft was last edited. */
  saved_at: string;
  /** edit_context.generated_ts of the baselines the draft was last checked against. */
  baseline_ts: string | null;
  entries: DraftState['entries'];
  /** The session's temporary-id floor (`DraftState.tempIdFloor`), once it issued one: a
   *  restore never issues those ids again. Absent for drafts that create no rows. */
  temp_id_floor?: number;
}

/** `saved` — mirrored; `idle` — nothing to mirror; `too_large` — over the cap, not
 *  mirrored (a reload loses it); `unavailable` — storage absent or throwing. */
export type DraftPersistence = 'idle' | 'saved' | 'too_large' | 'unavailable';

export interface RestoredDraft {
  /** Entries restored from sessionStorage. */
  count: number;
  /** When they were last edited (ISO). */
  savedAt: string;
}

function getStorage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    return null; // SecurityError: storage blocked for this origin
  }
}

function removeQuietly(storage: Storage, key: string): void {
  try {
    storage.removeItem(key);
  } catch {
    // Nothing more to do — the read side validates what it finds.
  }
}

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** Keep only well-formed entries; a hand-edited or truncated copy must not crash the page. */
function sanitizeEntries(raw: unknown): DraftState['entries'] {
  const out: DraftState['entries'] = {};
  if (!isObject(raw)) return out;
  for (const [rec, paths] of Object.entries(raw)) {
    // Negative ids are rows the draft creates (`alias:-1`).
    if (!/^[a-z_]+:-?\d+$/.test(rec) || !isObject(paths)) continue;
    const recEntries: Record<string, DraftEntry> = {};
    for (const [path, e] of Object.entries(paths)) {
      if (!isObject(e) || !('to' in e)) continue;
      const entry: DraftEntry = { from: e.from ?? null, to: e.to ?? null };
      const stale = e.stale;
      if (isObject(stale) && 'current' in stale) {
        const by = stale.changedBy;
        entry.stale = {
          current: stale.current ?? null,
          ...(stale.gone === true ? { gone: true as const } : {}),
          changedBy:
            isObject(by) && typeof by.label === 'string'
              ? {
                  label: by.label,
                  ts: typeof by.ts === 'string' ? by.ts : null,
                  source: typeof by.source === 'string' ? by.source : null,
                }
              : null,
        };
      }
      recEntries[path] = entry;
    }
    if (Object.keys(recEntries).length > 0) out[rec] = recEntries;
  }
  return out;
}

/** A stored temporary-id floor: a negative integer, else none. */
function storedFloor(v: unknown): number | null {
  return typeof v === 'number' && Number.isInteger(v) && v < 0 ? v : null;
}

export function readStoredDraft(
  kind: DraftKind,
  id: number,
): { entries: DraftState['entries']; savedAt: string; tempIdFloor?: number } | null {
  const storage = getStorage();
  if (!storage) return null;
  const key = draftStorageKey(kind, id);
  let raw: string | null;
  try {
    raw = storage.getItem(key);
  } catch {
    return null;
  }
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    removeQuietly(storage, key);
    return null;
  }
  if (
    !isObject(parsed) ||
    parsed.v !== DRAFT_STORAGE_VERSION ||
    parsed.kind !== kind ||
    parsed.id !== id ||
    typeof parsed.saved_at !== 'string'
  ) {
    removeQuietly(storage, key);
    return null;
  }
  const entries = sanitizeEntries(parsed.entries);
  if (Object.keys(entries).length === 0) {
    removeQuietly(storage, key);
    return null;
  }
  const floor = storedFloor(parsed.temp_id_floor);
  return { entries, savedAt: parsed.saved_at, ...(floor !== null ? { tempIdFloor: floor } : {}) };
}

export function writeStoredDraft(
  kind: DraftKind,
  id: number,
  draft: Omit<StoredDraft, 'v' | 'kind' | 'id'> | null,
): DraftPersistence {
  const storage = getStorage();
  if (!storage) return 'unavailable';
  const key = draftStorageKey(kind, id);
  try {
    if (!draft || Object.keys(draft.entries).length === 0) {
      storage.removeItem(key);
      return 'idle';
    }
    const stored: StoredDraft = { v: DRAFT_STORAGE_VERSION, kind, id, ...draft };
    const json = JSON.stringify(stored);
    if (json.length > DRAFT_STORAGE_MAX_CHARS) {
      storage.removeItem(key);
      return 'too_large';
    }
    storage.setItem(key, json);
    return 'saved';
  } catch {
    // Quota or blocked storage: drop the older copy so a reload can't resurrect it.
    removeQuietly(storage, key);
    return 'unavailable';
  }
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

/** The draft reducer behind a subscribable store: `dispatch` reduces at once and
 *  notifies only when the state object changed (no-op actions keep it). */
export interface DraftStore {
  getState: () => DraftState;
  dispatch: (action: DraftAction) => void;
  subscribe: (listener: () => void) => () => void;
}

export function createDraftStore(initial: DraftState): DraftStore {
  let state = initial;
  const listeners = new Set<() => void>();
  return {
    getState: () => state,
    dispatch: (action) => {
      const next = draftReducer(state, action);
      if (next === state) return;
      state = next;
      for (const l of [...listeners]) l();
    },
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

/** What a rebase compares the entries with, given the path-space baselines. */
export type FreshFrom = (
  baselines: Record<RecordKey, Record<string, unknown>>,
  entries: DraftState['entries'],
) => FreshValue[];

export interface DraftContextValue {
  kind: DraftKind;
  recordId: number;
  state: DraftState;
  dispatch: Dispatch<DraftAction>;
  /** Path-space baselines (null until the edit context loads). */
  baselines: Record<RecordKey, Record<string, unknown>> | null;
  baselineOf: (rec: RecordKey, path: string) => unknown;
  /** Set after a reload restored the draft; cleared by dismiss/discard or once it empties. */
  restored: RestoredDraft | null;
  dismissRestored: () => void;
  /** Discard the restored draft (undoable, like any Discard all). */
  discardRestored: () => void;
  persistence: DraftPersistence;
  /** A 409's drifted fields: mark their entries stale with the stored value and who
   *  changed it. */
  rebaseFromDrift: (drifted: RecordDriftedField[]) => void;
}

/** The identity-stable half of the draft, for components that must not re-render on
 *  every change (they subscribe to what they show — `useDraftRecord`). */
export interface DraftStableValue {
  kind: DraftKind;
  recordId: number;
  store: DraftStore;
  dispatch: Dispatch<DraftAction>;
  /** The latest baseline of a path — read at call time, never a render dependency. */
  baselineOf: (rec: RecordKey, path: string) => unknown;
}

const Ctx = createContext<DraftContextValue | null>(null);
const StableCtx = createContext<DraftStableValue | null>(null);

export interface DraftProviderProps {
  kind: DraftKind;
  recordId: number;
  /** edit_context `baselines` (`"<entity>:<id>"` → field → value); null while loading. */
  baselines: Record<RecordKey, Record<string, unknown>> | null | undefined;
  /** edit_context `generated_ts`, recorded with the mirror. */
  baselineTs?: string | null;
  /** Queued action key → the baseline field it changes, e.g. `{checkin: 'checkin_status'}`
   *  (catalog actions with `change: 'action'`). */
  actionFields?: Record<string, string>;
  /** What a rebase onto fresh baselines compares the entries with. Must be stable.
   *  Default `freshFromBaselines`. */
  freshFrom?: FreshFrom;
  children: ReactNode;
}

/** One draft per record: re-keyed by kind and id, so moving to another record never
 *  carries entries across. */
export function DraftProvider(props: DraftProviderProps) {
  return <DraftProviderInner key={`${props.kind}:${props.recordId}`} {...props} />;
}

const defaultFreshFrom: FreshFrom = (baselines) => freshFromBaselines(baselines);

function DraftProviderInner({
  kind,
  recordId,
  baselines,
  baselineTs = null,
  actionFields,
  freshFrom = defaultFreshFrom,
  children,
}: DraftProviderProps) {
  const [initial] = useState(() => readStoredDraft(kind, recordId));
  const [store] = useState(() =>
    createDraftStore(initial ? restoredDraftState(initial.entries, initial.tempIdFloor) : initialDraftState),
  );
  const state = useSyncExternalStore(store.subscribe, store.getState);
  const rawDispatch = store.dispatch;
  const [restored, setRestored] = useState<RestoredDraft | null>(() =>
    initial
      ? {
          count: draftCount({ ...initialDraftState, entries: initial.entries }),
          savedAt: initial.savedAt,
        }
      : null,
  );
  const [persistence, setPersistence] = useState<DraftPersistence>(initial ? 'saved' : 'idle');

  // When the user last changed the draft — a rebase is not an edit.
  const editedAtRef = useRef<string>(initial?.savedAt ?? new Date().toISOString());
  const dispatch = useCallback<Dispatch<DraftAction>>(
    (action) => {
      if (action.type !== 'REBASE') editedAtRef.current = new Date().toISOString();
      rawDispatch(action);
    },
    [rawDispatch],
  );

  const actionFieldsKey = JSON.stringify(actionFields ?? {});
  const stableActionFields = useMemo(
    () => JSON.parse(actionFieldsKey) as Record<string, string>,
    [actionFieldsKey],
  );
  const pathBase = useMemo(
    () => (baselines ? pathBaselines(baselines, stableActionFields) : null),
    [baselines, stableActionFields],
  );
  const pathBaseRef = useRef(pathBase);
  pathBaseRef.current = pathBase;

  // Fresh baselines (first load, refetch, re-seed after save) re-check every entry.
  useEffect(() => {
    if (pathBase) rawDispatch({ type: 'REBASE', fresh: freshFrom(pathBase, store.getState().entries) });
  }, [pathBase, freshFrom, rawDispatch, store]);

  // Mirror every change (not the restored state itself — it is already stored).
  const mirroredVersion = useRef(state.version);
  useEffect(() => {
    if (state.version === mirroredVersion.current) return;
    mirroredVersion.current = state.version;
    setPersistence(
      writeStoredDraft(kind, recordId, {
        saved_at: editedAtRef.current,
        baseline_ts: baselineTs,
        entries: state.entries,
        ...(state.tempIdFloor < 0 ? { temp_id_floor: state.tempIdFloor } : {}),
      }),
    );
  }, [kind, recordId, state, baselineTs]);

  const count = draftCount(state);
  useEffect(() => {
    if (count === 0) setRestored(null);
  }, [count]);

  const baselineOf = useCallback(
    (rec: RecordKey, path: string) => pathBase?.[rec]?.[path],
    [pathBase],
  );
  const dismissRestored = useCallback(() => setRestored(null), []);
  const discardRestored = useCallback(() => {
    dispatch({ type: 'DISCARD_ALL' });
    setRestored(null);
  }, [dispatch]);
  const rebaseFromDrift = useCallback(
    (drifted: RecordDriftedField[]) => rawDispatch({ type: 'REBASE', fresh: freshFromDrift(drifted) }),
    [rawDispatch],
  );

  const stable = useMemo<DraftStableValue>(
    () => ({
      kind,
      recordId,
      store,
      dispatch,
      baselineOf: (rec, path) => pathBaseRef.current?.[rec]?.[path],
    }),
    [kind, recordId, store, dispatch],
  );

  const value = useMemo<DraftContextValue>(
    () => ({
      kind,
      recordId,
      state,
      dispatch,
      baselines: pathBase,
      baselineOf,
      restored,
      dismissRestored,
      discardRestored,
      persistence,
      rebaseFromDrift,
    }),
    [
      kind,
      recordId,
      state,
      dispatch,
      pathBase,
      baselineOf,
      restored,
      dismissRestored,
      discardRestored,
      persistence,
      rebaseFromDrift,
    ],
  );
  return (
    <StableCtx.Provider value={stable}>
      <Ctx.Provider value={value}>{children}</Ctx.Provider>
    </StableCtx.Provider>
  );
}

export function useDraft(): DraftContextValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useDraft must be used inside <DraftProvider>');
  return v;
}

/** The identity-stable half of the draft — never re-renders with the draft itself. */
export function useDraftStable(): DraftStableValue {
  const v = useContext(StableCtx);
  if (!v) throw new Error('useDraftStable must be used inside <DraftProvider>');
  return v;
}

/** One record's entries (path → entry), or undefined when it has none. Re-renders only
 *  when THIS record's entries change — the subscription the itinerary rows build on. */
export function useDraftRecord(rec: RecordKey): Readonly<Record<string, DraftEntry>> | undefined {
  const { store } = useDraftStable();
  const getSnapshot = useCallback(() => store.getState().entries[rec], [store, rec]);
  return useSyncExternalStore(store.subscribe, getSnapshot);
}

// ---------------------------------------------------------------------------
// Field hook
// ---------------------------------------------------------------------------

export interface DraftField {
  rec: RecordKey;
  path: string;
  /** The stored value (edit context). */
  baseline: unknown;
  entry: DraftEntry | undefined;
  /** What edit mode shows: the draft's value, else the stored one. */
  value: unknown;
  changed: boolean;
  stale: StaleInfo | undefined;
  /** One undo step — or, inside a coalesce session, one step for the whole session.
   *  `from`: the value the change is made from where the record has no baseline yet (a
   *  row the draft creates starts from its defaults). */
  set: (to: unknown, opts?: { label?: string; from?: unknown }) => void;
  revert: () => void;
  keepMine: () => void;
  takeTheirs: () => void;
  /** Text inputs: call on focus / blur so a focus session's keystrokes undo as one. */
  beginCoalesce: () => void;
  endCoalesce: () => void;
}

let coalesceSeq = 0;

export function useDraftField(entity: string, id: number, path: string): DraftField {
  const { state, dispatch, baselineOf } = useDraft();
  const rec = recordKey(entity, id);
  const entry = state.entries[rec]?.[path];
  const baseline = baselineOf(rec, path);
  const token = useRef<string | null>(null);

  const set = useCallback(
    (to: unknown, opts?: { label?: string; from?: unknown }) =>
      dispatch({
        type: 'SET',
        changes: [{ rec, path, from: baseline === undefined && opts && 'from' in opts ? opts.from : baseline, to }],
        ...(opts?.label ? { label: opts.label } : {}),
        ...(token.current ? { coalesce: token.current } : {}),
      }),
    [dispatch, rec, path, baseline],
  );
  const revert = useCallback(
    () => dispatch({ type: 'REVERT', targets: [{ rec, path }] }),
    [dispatch, rec, path],
  );
  const keepMine = useCallback(
    () => dispatch({ type: 'KEEP_MINE', target: { rec, path } }),
    [dispatch, rec, path],
  );
  const takeTheirs = useCallback(
    () => dispatch({ type: 'TAKE_THEIRS', target: { rec, path } }),
    [dispatch, rec, path],
  );
  const beginCoalesce = useCallback(() => {
    coalesceSeq += 1;
    token.current = `${rec}|${path}#${coalesceSeq}`;
  }, [rec, path]);
  const endCoalesce = useCallback(() => {
    token.current = null;
  }, []);

  return {
    rec,
    path,
    baseline,
    entry,
    value: entry ? entry.to : baseline,
    changed: entry !== undefined,
    stale: entry?.stale,
    set,
    revert,
    keepMine,
    takeTheirs,
    beginCoalesce,
    endCoalesce,
  };
}

// ---------------------------------------------------------------------------
// Selector hooks — memoised on `state.version`, which changes on exactly every state
// change (no-op dispatches keep the same state object and version).
// ---------------------------------------------------------------------------

export interface DraftSummary {
  count: number;
  hasStale: boolean;
}

export function useDraftSummary(): DraftSummary {
  const { state } = useDraft();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => ({ count: draftCount(state), hasStale: hasStale(state) }), [state.version]);
}

export function useDraftGroups(order: RecordKey[] = []): DraftRecordGroup[] {
  const { state } = useDraft();
  const orderKey = order.join(',');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => groupByRecord(state, order), [state.version, orderKey]);
}

/** `stageOf` must be stable (module constant or memoised). */
export function useDraftStageCounts<S extends string>(
  stageOf: Readonly<Record<string, S>> | ((rec: RecordKey, path: string) => S | null | undefined),
): Partial<Record<S, number>> {
  const { state } = useDraft();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => countByStage(state, stageOf), [state.version, stageOf]);
}

/** Undo / redo with the label of what each would change ("Date 1 Oct → 2 Oct").
 *  `fmt` must be stable. */
export function useDraftUndoRedo(
  fmt?: DraftLabelFormatter,
): UndoRedoInfo & { undo: () => void; redo: () => void } {
  const { state, dispatch } = useDraft();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const info = useMemo(() => undoRedoInfo(state, fmt), [state.version, fmt]);
  const undo = useCallback(() => dispatch({ type: 'UNDO' }), [dispatch]);
  const redo = useCallback(() => dispatch({ type: 'REDO' }), [dispatch]);
  return { ...info, undo, redo };
}

/** The request body the draft compiles to (preview / apply). */
export function useCompiledChangeset(extras: ChangesetExtras = {}) {
  const { state } = useDraft();
  const extrasKey = JSON.stringify(extras);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => compileChangeset(state, extras), [state.version, extrasKey]);
}

/** The page-level unsaved-changes guard: only leaving the record (`basePath`, e.g.
 *  `/bookings/4711`) prompts — page tabs and views share the draft, so switching them is
 *  free. A router supports ONE blocker, so call this once, from the page. */
export function useDraftUnsavedGuard(
  basePath: string,
  { message, enabled = true }: { message?: string; enabled?: boolean } = {},
): { prompt: ReactElement | null } {
  const { count } = useDraftSummary();
  return useUnsavedChangesPrompt(enabled && count > 0, { basePath, ...(message ? { message } : {}) });
}
